import React, { useEffect, useMemo, useState } from "react";
import { GetCampusBuildingsList } from "../../services/CampusBuildingService";
import { MultiSelectDropdown } from "./FormFields";
import { useDebouncedApiEffect } from "../../hooks/useDebouncedApiEffect";
import { mapBuildingsToSelect } from "../../utils/CampusBuildingsUtil";

/**
 * A reusable multi‑select component for campus buildings.
 *
 * @component
 * @param {Object} props
 * @param {number|string} props.campusId - ID of the campus to fetch buildings for.
 * @param {Array} props.value - Currently selected building objects.
 * @param {Function} props.onChange - Callback when selection changes.
 * @param {string} [props.label="Buildings"] - Label for the field.
 * @param {string} [props.name="buildings"] - Field name (used for forms).
 * @param {boolean} [props.required=false] - Whether the field is required.
 * @param {number} [props.col_md=12] - Bootstrap medium column width.
 * @param {number} [props.col_lg=12] - Bootstrap large column width.
 * @returns {JSX.Element}
 */
const CampusBuildingMultiSelect = ({
    campusId,
    value = [],
    onChange,
    label = "Buildings",
    name = "buildings",
    required = false,
    col_md = 12,
    col_lg = 12,
}) => {
    const [buildingOptions, setBuildingOptions] = useState();
    const [isLoading, setIsLoading] = useState(true);

    /**
     * Purpose:
     * The deleted buildings must be removed from the value (values that are not present in buildingOptions).
     */
    useEffect(() => {
        if (!value?.length || !buildingOptions || !buildingOptions.length) {
            return;
        }

        const validBuildingIds = new Set(
            buildingOptions.map(option => option.value)
        );

        const filteredValue = value.filter(item =>
            validBuildingIds.has(item.value)
        );

        if (filteredValue.length !== value.length) {
            onChange(filteredValue);
        }
        
    }, [value?.length, buildingOptions?.length]);

    const fetchCampusBuildings = async ({ cancelToken }) => {
        setIsLoading(true);
        try {
            const resCampusBuildingsList = await GetCampusBuildingsList(campusId ? Number(campusId) : null, cancelToken);
            if (resCampusBuildingsList.data?.campusBuildingDetails) {
                const mappedBuildings = mapBuildingsToSelect(
                    resCampusBuildingsList.data.campusBuildingDetails
                );
                setBuildingOptions(mappedBuildings);
            }
        } catch (error) {
            console.error("CampusBuildingMultiSelect->Error loading campus buildings:", error);
            setBuildingOptions([]);
        } finally {
            setIsLoading(false);
        }
    };

    useDebouncedApiEffect(fetchCampusBuildings, [campusId]);

    const filteredBuildingOptions = useMemo(() => {
        const selectedCampusIds = value
            .map((building) => building.campus_id)
            .filter(Boolean);

        return (buildingOptions || []).filter((building) => {
            return (
                !selectedCampusIds.includes(building.campus_id) ||
                value.some((selectedBuilding) => selectedBuilding.value === building.value)
            );
        });
    }, [buildingOptions, value]);

    return (
        <MultiSelectDropdown
            name={name}
            label={label}
            value={value}
            options={filteredBuildingOptions}
            onChange={onChange}
            required={required}
            col_md={col_md}
            col_lg={col_lg}
            placeholder={isLoading ? "Loading buildings..." : "Select Buildings"}
            isDisabled={isLoading || !filteredBuildingOptions?.length}
            isLoading={isLoading}
            info={false}
        />
    );
};

export default CampusBuildingMultiSelect;